import React, { useState, useEffect } from 'react';
import { seriesApi, evaluationApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const HistoryPage = () => {
  const { user } = useAuth();
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    exerciseId: '',
    dateFrom: '',
    dateTo: '',
    limit: 50,
  });

  useEffect(() => {
    fetchSeries();
  }, [filters]);

  const fetchSeries = async () => {
    try {
      setLoading(true);
      const response = await seriesApi.getAll(filters);
      const payload = response?.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];
      setSeries(list);
    } catch (err) {
      console.error('Error fetching series:', err);
      setSeries([]);
    } finally {
      setLoading(false);
    }
  };

  const getExerciseName = (series) => {
    return series.exercise?.name || 'Exercício desconhecido';
  };

  const getEffortAndMood = (series) => {
    if (!series.evaluations || series.evaluations.length === 0) {
      return { effort: '-', mood: '-' };
    }
    const lastEval = series.evaluations[0];
    return { effort: lastEval.effort, mood: lastEval.mood };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAverageEffort = () => {
    if (series.length === 0) return 0;
    let total = 0;
    let count = 0;
    series.forEach(s => {
      if (s.evaluations && s.evaluations.length > 0) {
        total += s.evaluations[0].effort;
        count++;
      }
    });
    return count > 0 ? (total / count).toFixed(1) : 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-white mb-2">Histórico de Treinos</h1>
        <p className="text-gray-400">
          Todas as suas séries registradas
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card text-center">
          <div className="text-2xl font-bold text-blue-400">{series.length}</div>
          <div className="text-sm text-gray-400">Total de Séries</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-green-400">{getAverageEffort()}</div>
          <div className="text-sm text-gray-400">Média de Esforço</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-purple-400">
            {series.filter(s => s.evaluations && s.evaluations.length > 0).length}
          </div>
          <div className="text-sm text-gray-400">Séries Avaliadas</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Filtros</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Exercício
            </label>
            <select
              value={filters.exerciseId}
              onChange={(e) => setFilters({ ...filters, exerciseId: e.target.value })}
              className="input"
            >
              <option value="">Todos</option>
              {/* Would populate with actual exercises */}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              De
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Até
            </label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Limite
            </label>
            <select
              value={filters.limit}
              onChange={(e) => setFilters({ ...filters, limit: e.target.value })}
              className="input"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 text-sm font-medium text-gray-400">Exercício</th>
                <th className="text-left py-3 text-sm font-medium text-gray-400">Carga</th>
                <th className="text-left py-3 text-sm font-medium text-gray-400">Rep</th>
                <th className="text-left py-3 text-sm font-medium text-gray-400">Esforço</th>
                <th className="text-left py-3 text-sm font-medium text-gray-400">Ânimo</th>
                <th className="text-left py-3 text-sm font-medium text-gray-400">Data</th>
              </tr>
            </thead>
            <tbody>
              {series.map((s) => {
                const { effort, mood } = getEffortAndMood(s);
                return (
                  <tr
                    key={s.id}
                    className="border-b border-gray-800 last:border-0 hover:bg-gray-800/50"
                  >
                    <td className="py-3 text-white">{getExerciseName(s)}</td>
                    <td className="py-3 text-gray-300">{s.load}kg</td>
                    <td className="py-3 text-gray-300">{s.repetitions}</td>
                    <td className="py-3">
                      {effort !== '-' && (
                        <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm">
                          {effort}/5
                        </span>
                      )}
                    </td>
                    <td className="py-3">
                      {mood !== '-' && (
                        <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm">
                          {mood}/5
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-gray-400 text-sm">{formatDate(s.dateTime)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {series.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">Nenhuma série registrada ainda</p>
          <p className="text-gray-400 mt-2">
            <a href="/workout" className="text-blue-400 hover:underline">
              Registre sua primeira série
            </a>
          </p>
        </div>
      )}
    </div>
  );
};

export default HistoryPage;
